import { generatePrintHTML, generateQRCodeUrl, getPrintSettings } from './printTemplate';
import { generateOrderCode, removeVietnameseTones } from './formatters';
import { VIETNAM_BANKS } from '../constants';

/**
 * Convert HTML string to canvas using html2canvas approach
 * We'll render HTML in a hidden div and capture it
 */
export const htmlToImage = async (htmlString, width = 320) => {
  return new Promise((resolve, reject) => {
    // Create container
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = `${width}px`;
    container.style.background = '#fff';
    container.innerHTML = htmlString;
    document.body.appendChild(container);

    // Wait for images to load
    const images = container.querySelectorAll('img');
    const imagePromises = Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise((res) => {
        img.onload = res;
        img.onerror = res;
      });
    });

    Promise.all(imagePromises).then(async () => {
      try {
        // Dynamic import html2canvas
        const html2canvas = (await import('html2canvas')).default;
        
        const canvas = await html2canvas(container, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false
        });
        
        document.body.removeChild(container);
        
        // Convert canvas to blob
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create image blob'));
          }
        }, 'image/png', 1.0);
      } catch (error) {
        document.body.removeChild(container);
        reject(error);
      }
    });

    // Timeout fallback
    setTimeout(() => {
      if (document.body.contains(container)) {
        document.body.removeChild(container);
        reject(new Error('Timeout generating image'));
      }
    }, 10000);
  });
};

/**
 * Generate bill image HTML (matches print template)
 */
export const generateBillImageHTML = (order, shopSettings, printSettings = null) => {
  const settings = printSettings || getPrintSettings();
  const orderCode = generateOrderCode(order);
  const defaultBank = shopSettings?.bankAccounts?.find(b => b.isDefault);
  
  // Transfer content
  const transferPrefix = settings.transferPrefix || '';
  const transferContent = transferPrefix ? `${transferPrefix} ${orderCode}` : orderCode;
  
  // QR code URL
  const qrCodeUrl = defaultBank && settings.showQRCode ? 
    generateQRCodeUrl(order, defaultBank, settings, orderCode) : '';
  
  // Customer name display
  const customerName = order.customer?.name || order.customerName || 
    (order.table?.name || (order.tableNumber ? 'Bàn ' + order.tableNumber : 'Mang đi'));
  
  // Generate items HTML - match print template format
  const itemsHTML = order.items.map((item, idx) => {
    const optionPrice = item.selectedOption?.price || 0;
    const toppingsPrice = item.selectedToppings?.reduce((sum, t) => sum + (t.price || 0), 0) || 0;
    const basePrice = item.price - toppingsPrice - optionPrice;
    const itemTotal = item.price * item.quantity;
    
    let optionsHTML = '';
    if (item.selectedOption) {
      optionsHTML += `<div style="font-size: 10px; color: #333; padding-left: 10px; margin-bottom: 2px;">- ${item.selectedOption.name}${optionPrice > 0 ? ` (+${optionPrice.toLocaleString('vi-VN')}đ)` : ''}</div>`;
    }
    if (item.selectedToppings?.length > 0) {
      item.selectedToppings.forEach(t => {
        optionsHTML += `<div style="font-size: 10px; color: #333; padding-left: 10px; margin-bottom: 2px;">- ${t.name}${t.price > 0 ? ` (+${t.price.toLocaleString('vi-VN')}đ)` : ''}</div>`;
      });
    }
    
    return `
      <div style="margin-bottom: 14px;">
        <div style="font-weight: normal; font-size: 12px; margin-bottom: 4px;">${idx + 1}. ${item.name} (${basePrice.toLocaleString('vi-VN')}đ)</div>
        ${optionsHTML}
        <div style="display: flex; justify-content: space-between; font-size: 12px; margin-top: 6px;">
          <span>x${item.quantity}</span>
          <span>${itemTotal.toLocaleString('vi-VN')} đ</span>
        </div>
      </div>
    `;
  }).join('');

  // Total section with discount
  let totalSectionHTML = '';
  
  // Calculate item subtotal
  const itemsSubtotal = order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  // Handle extraFee
  const extraFeeAmount = typeof order.extraFee === 'object' ? (order.extraFee?.amount || 0) : (order.extraFee || 0);
  const extraFeeNote = typeof order.extraFee === 'object' ? order.extraFee?.description : (order.extraFeeNote || '');
  
  // Handle manual discount
  const manualDiscountAmount = order.manualDiscount?.amount || 0;
  const manualDiscountType = order.manualDiscount?.discountType || 'fixed';
  const manualDiscountValue = order.manualDiscount?.value || 0;
  
  // Check if has any discount
  const hasPromotionDiscount = order.discount && order.discount.percentage > 0;
  const hasManualDiscount = manualDiscountAmount > 0;
  const hasAnyDiscount = hasPromotionDiscount || hasManualDiscount;
  
  // Show original price if has any discount
  if (hasAnyDiscount) {
    const originalAmount = itemsSubtotal + (order.discount?.amount || 0) + manualDiscountAmount;
    totalSectionHTML += `
      <div style="display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 6px;">
        <span>Giá gốc</span>
        <span>${originalAmount.toLocaleString('vi-VN')} đ</span>
      </div>
    `;
  }
  
  // Show promotion discount if exists
  if (hasPromotionDiscount) {
    totalSectionHTML += `
      <div style="display: flex; justify-content: space-between; font-size: 11px; color: #dc2626; margin-bottom: 6px;">
        <span>KM (${order.discount.name || order.discount.percentage + '%'})</span>
        <span>-${(order.discount.amount || 0).toLocaleString('vi-VN')} đ</span>
      </div>
    `;
  }
  
  // Show manual discount if exists
  if (hasManualDiscount) {
    const discountLabel = manualDiscountType === 'percent' ? `${manualDiscountValue}%` : 'cố định';
    totalSectionHTML += `
      <div style="display: flex; justify-content: space-between; font-size: 11px; color: #dc2626; margin-bottom: 6px;">
        <span>Giảm giá (${discountLabel})</span>
        <span>-${manualDiscountAmount.toLocaleString('vi-VN')} đ</span>
      </div>
    `;
  }
  
  // Show extra fee if exists
  if (extraFeeAmount > 0) {
    // If has discount, show subtotal first
    if (hasAnyDiscount) {
      const subtotalAfterDiscount = itemsSubtotal - (order.discount?.amount || 0) - manualDiscountAmount;
      totalSectionHTML += `
        <div style="display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 6px;">
          <span>Tạm tính</span>
          <span>${subtotalAfterDiscount.toLocaleString('vi-VN')} đ</span>
        </div>
      `;
    }
    totalSectionHTML += `
      <div style="display: flex; justify-content: space-between; font-size: 11px; color: #ea580c; margin-bottom: 6px;">
        <span>Phụ phí${extraFeeNote ? ` (${extraFeeNote})` : ''}</span>
        <span>+${extraFeeAmount.toLocaleString('vi-VN')} đ</span>
      </div>
    `;
  }

  const totalItems = order.items.reduce((sum, item) => sum + item.quantity, 0);
  const paidAt = order.paidAt ? new Date(order.paidAt) : new Date();
  const qrSize = settings.qrSize || 150;

  return `
    <div style="font-family: Arial, sans-serif; padding: 8px; background: #fff; color: #000; width: 302px; line-height: 1.6;">
      <!-- Header -->
      <div style="text-align: center; margin-bottom: 6px;">
        ${settings.showLogo ? (shopSettings?.avatar 
          ? `<img src="${shopSettings.avatar}" style="width: 50px; height: 50px; border-radius: 50%; object-fit: cover; display: block; margin: 0 auto;" alt="Logo">`
          : `<div style="width: 50px; height: 50px; background: #16a34a; border-radius: 50%; margin: 0 auto; display: flex; align-items: center; justify-content: center;">
              <span style="color: #fff; font-weight: bold; font-size: 18px;">GC</span>
            </div>`
        ) : ''}
        <div style="font-size: 14px; font-weight: bold; color: #16a34a;">${shopSettings?.shopName || 'Green Cafe'}</div>
        ${shopSettings?.phone ? `<div style="font-size: 11px; color: #666; margin-bottom: 12px;">${shopSettings.phone}</div>` : ''}
      </div>
      
      <!-- Divider -->
      <div style="border-top: 1px dashed #aaa; margin: 10px 0 6px 0;"></div>
      
      <!-- Title -->
      <div style="text-align: center; margin-bottom: 14px;">
        <div style="font-size: 13px; font-weight: bold; margin-bottom: 6px;">HÓA ĐƠN BÁN HÀNG</div>
        <div style="font-size: 10px; color: #666;">${orderCode} - ${paidAt.toLocaleString('vi-VN')}</div>
      </div>
      
      <!-- Customer Info -->
      <div style="display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 14px;">
        <span>Khách:</span>
        <span>${customerName}</span>
      </div>
      
      <!-- Divider -->
      <div style="border-top: 1px dashed #aaa; margin: 10px 0 6px 0;"></div>
      
      <!-- Items -->
      <div style="margin-bottom: 4px;">
        ${itemsHTML}
      </div>
      
      <!-- Divider -->
      <div style="border-top: 1px dashed #aaa; margin: 10px 0 6px 0;"></div>
      
      <!-- Total Section -->
      <div>
        ${totalSectionHTML}
        <div style="display: flex; justify-content: space-between; font-size: 13px; font-weight: bold; border-top: 1px solid #000; padding-top: 0; margin-top: 6px; margin-bottom: 14px;">
          <span>Tổng cộng (${totalItems}SP)</span>
          <span>${(order.totalAmount || 0).toLocaleString('vi-VN')} đ</span>
        </div>
      </div>
      
      ${qrCodeUrl ? `
      <!-- Divider -->
      <div style="border-top: 1px dashed #aaa; margin: 10px 0 6px 0;"></div>
      
      <!-- QR Section -->
      <div style="display: flex; align-items: center; gap: 12px; margin: 14px 0 10px 0;">
        <img src="${qrCodeUrl}" style="width: ${qrSize}px; height: ${qrSize}px; flex-shrink: 0;" alt="QR Code">
        ${settings.showBankInfo && defaultBank ? `
        <div style="font-size: 10px; flex: 1; text-align: left;">
          <div style="font-weight: bold; margin-bottom: 3px;">${defaultBank.bankName}</div>
          <div style="margin-bottom: 2px;">${defaultBank.accountName}</div>
          <div style="margin-bottom: 2px;"><b>${defaultBank.accountNumber}</b></div>
          <div>ND: <b>${transferContent}</b></div>
        </div>
        ` : ''}
      </div>
      ` : ''}
      
      ${settings.showTransferNote ? `
      <!-- Transfer Note -->
      <div style="text-align: center; font-size: 10px; padding: 0 6px 14px 6px; border: 1px dashed #aaa; margin: 10px 0 4px 0;">
        Thanh toán tiền mặt hoặc chuyển khoản
      </div>
      ` : ''}
      
      ${settings.footerText ? `
      <div style="text-align: center; margin-top: 0; font-size: 10px; color: #666;">
        ${settings.footerText}
      </div>
      ` : ''}
    </div>
  `;
};

/**
 * Generate QR payment image HTML
 */
export const generateQRImageHTML = (order, shopSettings) => {
  const settings = getPrintSettings();
  const defaultBank = shopSettings?.bankAccounts?.find(b => b.isDefault);
  const orderCode = generateOrderCode(order);
  
  if (!defaultBank) {
    return null;
  }
  
  const qrCodeUrl = generateQRCodeUrl(order, defaultBank, { ...settings, showQRCode: true }, orderCode);
  const transferPrefix = settings.transferPrefix || '';
  const transferContent = transferPrefix ? `${transferPrefix} ${orderCode}` : orderCode;

  return `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; background: #fff; color: #000; text-align: center; width: 320px;">
      <!-- Header -->
      <div style="margin-bottom: 12px;">
        <div style="font-size: 20px; font-weight: bold; color: #16a34a;">${shopSettings?.shopName || 'Green Cafe'}</div>
        <div style="font-size: 12px; color: #666; margin-top: 4px;">Sử dụng app ngân hàng quét mã để thanh toán</div>
      </div>
      
      <!-- QR Code - Centered -->
      <div style="display: flex; justify-content: center; margin-bottom: 16px;">
        <img src="${qrCodeUrl}" style="width: 220px; height: 220px;" alt="VietQR">
      </div>
      
      <!-- Amount Box -->
      <div style="background: #16a34a; padding: 4px 16px 12px 16px; border-radius: 10px; margin-bottom: 12px;">
        <div style="font-size: 12px; color: rgba(255,255,255,0.9); text-align: center;">Số tiền thanh toán</div>
        <div style="font-size: 20px; font-weight: bold; color: #ffffff; text-align: center;">${(order.totalAmount || 0).toLocaleString('vi-VN')}đ</div>
      </div>
      
      <!-- Bank Info -->
      <div style="background: #f3f4f6; border-radius: 10px; padding: 6px 16px 14px 16px; text-align: left;">
        <div style="font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 8px;">${defaultBank.bankName}</div>
        <div style="display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 4px;">
          <span style="color: #6b7280;">Chủ TK:</span>
          <span style="font-weight: 500;">${defaultBank.accountName}</span>
        </div>
        <div style="display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 4px;">
          <span style="color: #6b7280;">Số TK:</span>
          <span style="font-weight: 600; color: #16a34a;">${defaultBank.accountNumber}</span>
        </div>
        <div style="display: flex; justify-content: space-between; font-size: 13px;">
          <span style="color: #6b7280;">Nội dung CK:</span>
          <span style="font-weight: 600; color: #2563eb;">${transferContent}</span>
        </div>
      </div>
    </div>
  `;
};

/**
 * Share image using Web Share API
 */
export const shareImage = async (imageBlob, filename, title, text) => {
  const file = new File([imageBlob], filename, { type: 'image/png' });
  
  // Check if Web Share API with files is supported
  if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: title,
        text: text
      });
      return { success: true };
    } catch (error) {
      if (error.name === 'AbortError') {
        return { success: false, cancelled: true };
      }
      throw error;
    }
  } else {
    // Fallback: Download image
    const url = URL.createObjectURL(imageBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return { success: true, downloaded: true };
  }
};

/**
 * Main share function for bill - uses inline styles for html2canvas compatibility
 */
export const shareBill = async (order, shopSettings) => {
  // Use inline styles HTML for better html2canvas rendering
  const html = generateBillImageHTML(order, shopSettings);
  const orderCode = generateOrderCode(order);
  
  // Use 302px width to match print template
  const imageBlob = await htmlToImage(html, 302);
  return shareImage(
    imageBlob,
    `hoadon-${orderCode}.png`,
    `Hóa đơn ${orderCode}`,
    `Hóa đơn ${orderCode} - ${(order.totalAmount || 0).toLocaleString('vi-VN')}đ`
  );
};

/**
 * Main share function for QR payment
 */
export const shareQRPayment = async (order, shopSettings) => {
  const html = generateQRImageHTML(order, shopSettings);
  
  if (!html) {
    throw new Error('Chưa cấu hình tài khoản ngân hàng');
  }
  
  const orderCode = generateOrderCode(order);
  
  const imageBlob = await htmlToImage(html, 320);
  return shareImage(
    imageBlob,
    `qr-thanhtoan-${orderCode}.png`,
    `QR Thanh toán ${orderCode}`,
    `Quét mã để thanh toán ${order.totalAmount.toLocaleString('vi-VN')}đ`
  );
};

/**
 * Generate QR code URL for debt payment
 */
export const generateDebtQRCodeUrl = (amount, transferContent, defaultBank) => {
  if (!defaultBank) return '';
  
  const bank = VIETNAM_BANKS.find(b => b.code === defaultBank.bankCode);
  if (!bank) return '';
  
  return `https://img.vietqr.io/image/${bank.bin}-${defaultBank.accountNumber}-compact.png?amount=${amount}&addInfo=${encodeURIComponent(transferContent)}`;
};

/**
 * Generate debt QR payment image HTML
 * @param {string} customerId - ID của khách hàng
 * @param {string} customerName - Tên khách hàng (để hiển thị)
 * @param {number} totalDebt - Tổng nợ
 * @param {object} shopSettings - Cấu hình shop
 */
export const generateDebtQRImageHTML = (customerId, customerName, totalDebt, shopSettings) => {
  const defaultBank = shopSettings?.bankAccounts?.find(b => b.isDefault);
  
  if (!defaultBank) {
    return null;
  }
  
  // Transfer content: NO-{customerId} (dễ parse trong webhook)
  const transferContent = `NO ${customerId}`;
  
  const qrCodeUrl = generateDebtQRCodeUrl(totalDebt, transferContent, defaultBank);

  return `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; background: #fff; color: #000; text-align: center; width: 320px;">
      <!-- Header -->
      <div style="margin-bottom: 12px;">
        <div style="font-size: 20px; font-weight: bold; color: #16a34a;">${shopSettings?.shopName || 'Green Cafe'}</div>
        <div style="font-size: 12px; color: #666; margin-top: 4px;">Thanh toán nợ - ${customerName}</div>
      </div>
      
      <!-- QR Code - Centered -->
      <div style="display: flex; justify-content: center; margin-bottom: 16px;">
        <img src="${qrCodeUrl}" style="width: 220px; height: 220px;" alt="VietQR">
      </div>
      
      <!-- Amount Box -->
      <div style="background: #ea580c; padding: 4px 16px 12px 16px; border-radius: 10px; margin-bottom: 12px;">
        <div style="font-size: 12px; color: rgba(255,255,255,0.9); text-align: center;">Tổng tiền cần thanh toán</div>
        <div style="font-size: 20px; font-weight: bold; color: #ffffff; text-align: center;">${totalDebt.toLocaleString('vi-VN')}đ</div>
      </div>
      
      <!-- Bank Info -->
      <div style="background: #f3f4f6; border-radius: 10px; padding: 6px 16px 14px 16px; text-align: left;">
        <div style="font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 8px;">${defaultBank.bankName}</div>
        <div style="display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 4px;">
          <span style="color: #6b7280;">Chủ TK:</span>
          <span style="font-weight: 500;">${defaultBank.accountName}</span>
        </div>
        <div style="display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 4px;">
          <span style="color: #6b7280;">Số TK:</span>
          <span style="font-weight: 600; color: #16a34a;">${defaultBank.accountNumber}</span>
        </div>
        <div style="display: flex; justify-content: space-between; font-size: 13px;">
          <span style="color: #6b7280;">Nội dung CK:</span>
          <span style="font-weight: 600; color: #2563eb;">${transferContent}</span>
        </div>
      </div>
      
      <!-- Customer Info -->
      <div style="margin-top: 12px; padding: 8px; background: #fef3c7; border-radius: 8px;">
        <div style="font-size: 11px; color: #92400e;">Khách hàng: <b>${customerName}</b></div>
      </div>
    </div>
  `;
};

/**
 * Main share function for debt QR payment
 * @param {string} customerId - ID của khách hàng
 * @param {string} customerName - Tên khách hàng
 * @param {number} totalDebt - Tổng nợ
 * @param {object} shopSettings - Cấu hình shop
 */
export const shareDebtQR = async (customerId, customerName, totalDebt, shopSettings) => {
  const html = generateDebtQRImageHTML(customerId, customerName, totalDebt, shopSettings);
  
  if (!html) {
    throw new Error('Chưa cấu hình tài khoản ngân hàng');
  }
  
  const customerNameNoAccent = removeVietnameseTones(customerName).replace(/\s+/g, '');
  
  const imageBlob = await htmlToImage(html, 320);
  return shareImage(
    imageBlob,
    `qr-thanhtoan-no-${customerNameNoAccent}.png`,
    `QR Thanh toán nợ - ${customerName}`,
    `Quét mã để thanh toán nợ ${totalDebt.toLocaleString('vi-VN')}đ`
  );
};

/**
 * Check if sharing is supported
 */
export const isShareSupported = () => {
  return !!(navigator.share);
};

/**
 * Check if file sharing is supported
 */
export const isFileShareSupported = () => {
  if (!navigator.share || !navigator.canShare) return false;
  
  // Create a test file to check support
  const testFile = new File(['test'], 'test.png', { type: 'image/png' });
  return navigator.canShare({ files: [testFile] });
};
